import { CorporateShareHolder } from 'bff/src/domain/model/compliance.model';
import { useState } from 'react';
import { isValidCNPJ } from 'utils/cpnjValidation';

export const useCorporateShareholder = (): [
  typeof corporateShareholder,
  typeof setCorporateShareholder,
  typeof validate,
  typeof errors,
] => {
  const [legalName, setLegalName] =
    useState<CorporateShareHolder['legalName']>('');
  const [legalNameError, setLegalNameError] = useState<string>();

  const [document, setDocument] =
    useState<CorporateShareHolder['document']>('');
  const [documentError, setDocumentError] = useState<string>();

  const [participation, setParticipation] =
    useState<CorporateShareHolder['participation']>(0);
  const [participationError, setParticipationError] = useState<string>();

  const [address, setAddress] = useState<CorporateShareHolder['address']>('');
  const [addressError, setAddressError] = useState<string>();

  const [country, setCountry] = useState<CorporateShareHolder['country']>('BR');
  const [countryError, setCountryError] = useState<string>();

  const resetState = () => {
    setLegalName('');
    setLegalNameError(undefined);
    setDocument('');
    setDocumentError(undefined);
    setParticipation(0);
    setParticipationError(undefined);
    setAddress('');
    setAddressError(undefined);
    setCountry('BR');
    setCountryError(undefined);
  };

  const validateLegalName = () => {
    if (!legalName) {
      setLegalNameError('Campo é obrigatório!');
      return false;
    }
    setLegalNameError('');
    return true;
  };

  const validateDocument = () => {
    if (!document) {
      setDocumentError('Campo é obrigatório!');
      return false;
    }
    if (!isValidCNPJ(document)) {
      setDocumentError('CNPJ não é valido!');
      return false;
    }
    setDocumentError('');
    return true;
  };

  const validateParticipation = () => {
    if (!participation) {
      setParticipationError('Campo é obrigatório!');
      return false;
    }
    if (participation <= 0 || participation > 100) {
      setParticipationError(
        'Utilize um número maior que zero e menor ou igual a 100',
      );
      return false;
    }
    setParticipationError('');
    return true;
  };

  const validateAddress = () => {
    if (!address) {
      setAddressError('Campo é obrigatório!');
      return false;
    }
    if (address.split(' ').length < 2) {
      setAddressError('Campo deve conter um endereço válido');
      return false;
    }
    setAddressError('');
    return true;
  };

  const validateCountry = (): boolean => {
    if (!country.trim()) {
      setCountryError('Campo é obrigatório!');
      return false;
    }

    setCountryError('');
    return true;
  };

  const validate = (): boolean => {
    const isLegalNameValid = validateLegalName();
    const isDocumentValid = validateDocument();
    const isParticipationValid = validateParticipation();
    const isAddressValid = validateAddress();
    const isCountryValid = validateCountry();

    return (
      isLegalNameValid &&
      isDocumentValid &&
      isParticipationValid &&
      isAddressValid &&
      isCountryValid
    );
  };

  const corporateShareholder = {
    legalName,
    document,
    participation,
    address,
    country,
  };

  const setCorporateShareholder = {
    setLegalName,
    setLegalNameError,
    setDocument,
    setDocumentError,
    setParticipation,
    setParticipationError,
    setAddress,
    setAddressError,
    setCountry,
    setCountryError,
    resetState,
    setAll: (data: Partial<CorporateShareHolder>) => {
      if (data.legalName !== undefined) setLegalName(data.legalName);
      if (data.document !== undefined) setDocument(data.document);
      if (data.participation !== undefined)
        setParticipation(data.participation);
      if (data.address !== undefined) setAddress(data.address);
      if (data.country !== undefined) setCountry(data.country);
    },
  };

  const errors = {
    legalNameError,
    documentError,
    participationError,
    addressError,
    countryError,
  };

  return [corporateShareholder, setCorporateShareholder, validate, errors];
};
